import { Router } from '@angular/router';
import { Dictionary } from './../../shared/models/classes/dictionary/dictionary';
import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { UntypedFormBuilder, Validators, UntypedFormGroup, ValidationErrors, UntypedFormControl, UntypedFormArray, FormArray, ControlContainer, FormControl } from '@angular/forms';
import * as moment from 'moment';
import { UiService } from '@services/ui.service';
import { Order } from '@classes/order/order';
import { OrdersService } from '@services/orders.service';
import { AuthService } from '@services/auth.service';
import { RoleName } from '@enums/role-name.enum';
import { UsersService } from '@services/users.service';
import { ConfirmValidMatcher } from '@classes/validations/confirm-valid-matcher';
import { Client } from '@classes/client';
import { ClientsService } from '@services/clients.service';
import { Site } from '@classes/site';
import { SitesService } from '@services/sites.service';
import { DictionaryValuesService } from '@services/dictionaryValues.service';
import { Address } from '@classes/address';
import { AddressesService } from '@services/addresses.service';
import { DictionaryValue } from '@classes/dictionary/dictionaryValue';
import { MatLegacyAutocomplete as MatAutocomplete, MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { User } from '@classes/user/user';
import { LabileBlood } from '@enums/labile-blood.enum';
import { OrderType } from '@enums/order-type.enum';

@Component({
  selector: 'app-order-create',
  templateUrl: './order-create.component.html',
  styleUrls: ['./order-create.component.scss']
})
export class OrderCreateComponent extends FormEditBaseComponent implements OnInit {

  clients: Client[];
  sites: Site[];
  filteredSites: Site[] = [];
  selectedClient: Client;
  allTransportTypes: DictionaryValue[];
  urgencyLevels: DictionaryValue[];
  senders: Address[];
  receivers: Address[];
  drivers: User[];
  currentUser?: User;

  order: Order;

  stringMaxLength = 50;
  commentMaxLength = 500;
  codeMaxLength = 20;
  isRestricted: boolean;
  isClient: boolean;
  isAdmin: boolean;
  isDispatcher: boolean;
  isAdminWithOneClient: boolean;
  mustCheckDate: boolean;
  confirmValidMatcher = new ConfirmValidMatcher();
  canEditTourPrices: boolean;

  invalidFields: string[] = [];
  invalidMessage: string = '';

  transportTypesSelected: DictionaryValue[] = [];
  devices: DictionaryValue[];
  filteredTransportTypes: Observable<DictionaryValue[]>;
  transportTypesCtrl = new UntypedFormControl();
  selectable = true;
  removable = true;
  addOnBlur = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];

  clientForm = new UntypedFormGroup({
    client: new UntypedFormControl('', Validators.required)
  })

  canChooseValue = true;
  canAddOtherAddress = true;

  @ViewChild('deviceInput') deviceInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private authSrv: AuthService,
    private usersSrv: UsersService,
    private clientsSrv: ClientsService,
    private ordersSrv: OrdersService,
    private sitesSrv: SitesService,
    private dictionarySrv: DictionaryValuesService,
    private addressesSrv: AddressesService,
    private router: Router
  ) {
    super(uiSrv, fb);

  }

  ngOnInit(): void {
    super.ngOnInit();

    // tslint:disable-next-line: max-line-length
    this.isRestricted = this.authSrv.getUser().role.name !== RoleName.super_admin && this.authSrv.getUser().role.name !== RoleName.bizzdev;
    this.isClient = this.authSrv.getUser().role.name === RoleName.client;

    // Observer les changements de client
    this.clientForm.get('client').valueChanges.subscribe(client => {
      if (client && typeof client !== 'string') {
        this.chooseClient(client);
      } else {
        // Client supprimé ou invalide - réinitialiser et masquer le formulaire
        this.selectedClient = null!;
        this.filteredSites = [];
        this.canChooseValue = false;
        if (this.editForm) {
          this.editForm.get('site')!.setValue(null);
        }
      }
    });
    this.isAdmin =
      this.authSrv.getUser().role.name === RoleName.bizzdev ||
      this.authSrv.getUser().role.name === RoleName.super_admin ||
      this.authSrv.getUser().role.name === RoleName.admin ||
      this.authSrv.getUser().role.name === RoleName.admin_subcontractor;
    this.isDispatcher =
      this.authSrv.getUser().role.name !== RoleName.client &&
      this.authSrv.getUser().role.name !== RoleName.admin_client;

    this.canEditTourPrices =
      this.authSrv.getUser().role.name === RoleName.bizzdev
      || this.authSrv.getUser().role.name === RoleName.super_admin
      || this.authSrv.getUser().role.name === RoleName.admin
      || this.authSrv.getUser().role.name === RoleName.admin_subcontractor
      || this.authSrv.getUser().role.name === RoleName.admin_client
      || this.authSrv.getUser().role.name === RoleName.client;

      const hasFullAccess = this.authSrv.getUser().role.name === RoleName.bizzdev
        || this.authSrv.getUser().role.name === RoleName.super_admin;

      let clients = [];
      clients = this.authSrv.getDecodedToken().client.split(',');
      
      if (hasFullAccess) {
        this.canChooseValue = false;
        this.isAdminWithOneClient = false;
      } else if (clients.length > 1) {
        this.canChooseValue = false;
        this.isAdminWithOneClient = false;
      } else {
        // Utilisateur avec une seule agence : auto-sélectionner
        this.canChooseValue = true;
        this.isAdminWithOneClient = true;
      }

    this.initSubscriptions();
  }

  isControlSet(control: string): boolean {
    return !!this.editForm.get(control);
  }

  onFinish(): void {
    this.editFinished.emit();
    this.router.navigate(['/orders/list']);
  }

  onSubmit(): void {
    if (this._isDatesValid()) {
      this.isLoading = true;
      this.create();
    }
  }

  private _isDatesValid(): boolean {
    if (this.mustCheckDate) {

      let dateNow = new Date().setHours(0, 0, 0, 0);
      let startDate = new Date(this.editForm.controls.schedule.value[0].startDate).setHours(0, 0, 0, 0);

      if (startDate < dateNow) {
        alert("La date ne peut pas être antérieure à la date du jour.");
        return false;
      }
    }

    let startDate = new Date(this.editForm.controls.schedule.value[0].startDate)
    let startTime = this.editForm.controls.schedule.value[0].startTime.split(':');
    startDate.setHours(startTime[0], startTime[1]);

    if (this.mustCheckDate) {
      let dateNow = new Date();
      let diff = startDate.getTime() - dateNow.getTime();

      if (diff <= 20 * 60 * 1000) {
        alert("L'heure de début doit être au moins 20 minutes après l'heure actuelle.");
        return false;
      }
    }

    if (this.editForm.controls.requestedDeliveryDate.value && this.editForm.controls.requestedDeliveryTime.value) {
      let requestedDeliveryDate = new Date(this.editForm.controls.requestedDeliveryDate.value);
      let requestedDeliveryTime = this.editForm.controls.requestedDeliveryTime.value.split(':');
      requestedDeliveryDate.setHours(requestedDeliveryTime[0], requestedDeliveryTime[1]);
      if (requestedDeliveryDate.getTime() - startDate.getTime() < 0) {
        alert("La date de livraison ne peut pas être antérieure à la date de départ.");
        return false;
      }
    }

    return true;
  }

  private create(): void {
    const formValues = this.editForm.value;

    const sitesPromise = new Promise((resolve, reject) => {
      if (typeof formValues.site === 'string' && this.isDispatcher) {
        let newSite = new Site(
          formValues.site,
          [this.urgencyLevels[0].client],
          false
        );

        this.sitesSrv.post(newSite).subscribe(newSiteFromSrv => {
          formValues.site = newSiteFromSrv;
          resolve(true);
        })
      } else {
        resolve(true);
      }
    });

    Promise.all([sitesPromise])
      .then(() => {
        if (typeof formValues.sender.address === 'string') {
          formValues.sender.otherAddress = formValues.sender.address;
          formValues.sender.address = null;
        } else {
          formValues.sender.otherAddress = null;
        }

        if (formValues.type === "isTour") {
          formValues.sender.isDelivery = true;
          formValues.sender.isPickup = true;
        }

        formValues.receivers.map(receiver => {
          if (typeof receiver.address === 'string') {
            receiver.otherAddress = receiver.address;
            receiver.address = null;
          } else {
            receiver.otherAddress = null;
          }

          if (formValues.type === "isTour") {
            receiver.isDelivery = true;
            receiver.isPickup = true;
          }
          else {
            if (receiver.type === 'isDelivery') {
              receiver.isDelivery = true;
              receiver.isPickup = false;
            } else {
              receiver.isDelivery = false;
              receiver.isPickup = true;
            }
          }
        })

        let callDate = null;

        if (formValues.callDate) {
          let callTime = formValues.callDate.split(':');
          let dateNow = moment(new Date());
          callDate = dateNow.hours(callTime[0]).minutes(callTime[1]).utc().toDate();
        }


        this.order = new Order(
          formValues.site,
          formValues.urgencyLevel,
          this.scheduleToDate(formValues.schedule[0]),
          formValues.ordererPhone,
          formValues.ordererName?.trim(),
          formValues.sender,
          formValues.receivers,
          this.transportTypesSelected,
          1,
          formValues.ordererComment,
          callDate
        );

        this.order.client = this.clientForm.get('client')?.value;

        if (formValues.requestedDeliveryDate && formValues.requestedDeliveryTime) {
          const requestedDeliveryDate: moment.Moment = formValues.requestedDeliveryDate.local();
          const requestedDeliveryTime: number[] = formValues.requestedDeliveryTime.split(':').map(s => parseInt(s, 10));
          this.order.requestedDeliveryDate = requestedDeliveryDate.hours(requestedDeliveryTime[0]).minutes(requestedDeliveryTime[1]).utc().toDate();
        }

        if (formValues.name) {
          this.order.name = formValues.name;
        }

        if (formValues.type === "isTour") {
          this.order.type = OrderType.tour;
          this.order.code = formValues.code;
          if (this.canEditTourPrices) {
            this.order.price = formValues.price;
            this.order.extraStepPrice = formValues.extraStepPrice;
          }

          if (formValues.driver)
            this.order.driver = formValues.driver;
        }

        this.ordersSrv.post(this.order)
          .subscribe(
            (orderCreated) => {
              this.isLoading = false;
              this.uiSrv.showSnackbar('orders.create.created', true);
              this.order = orderCreated;

              this.onFinish();
            },
            () => this.order = null
          );
      });
  }

  private initSubscriptions(): void {
    this.isLoading = true;

    const currentUserPromise = new Promise((resolve, reject) => {
      this.usersSrv.getCurrentUser()
        .subscribe(userFromSrv => {
          this.currentUser = userFromSrv;
          resolve(true);
        })
    })

    const addressPromise = new Promise((resolve, reject) => {
      this.addressesSrv.getList()
        .subscribe(addressessFromSrv => {
          this.senders = addressessFromSrv.filter(address => address.isSender);
          this.receivers = addressessFromSrv.filter(address => address.isReceiver);
          resolve(true);
        });
    });

    const clientsPromise = new Promise((resolve, reject) => {
      this.clientsSrv.getList()
        .subscribe(clientsFromSrv => {
          this.clients = clientsFromSrv;
          resolve(true);
        });
    });

    const driversPromise = new Promise((resolve, reject) => {
      this.usersSrv.getList(null, true).subscribe((usersFromSrv) => {
        this.drivers = usersFromSrv.filter(user => user.role.name !== RoleName.admin_client && user.role.name !== RoleName.client);
        resolve(true);
      });
    });

    if (this.canChooseValue) {
      const sitesPromise = new Promise((resolve, reject) => {
        this.sitesSrv.getNonTransmissionOnlyList()
          .subscribe(sitesFromSrv => {
            this.sites = sitesFromSrv;
            resolve(true);
          });
      });

      const typeTransportsPromise = new Promise((resolve, reject) => {
        this.dictionarySrv.getTransportList()
          .subscribe(transportsFromSrv => {
            this.allTransportTypes = transportsFromSrv;
            // this.filteredTransportTypes = transportsFromSrv;
            this.filteredTransportTypes = this.transportTypesCtrl.valueChanges.pipe(
              startWith(null),
              map((fruit: string | null) => fruit ? this._filter(fruit) : this.allTransportTypes.slice()));
            resolve(true);
          });
      });

      const urgencyLevelsPromise = new Promise((resolve, reject) => {
        this.dictionarySrv.getUrgencyList()
          .subscribe(urgencyLevelsFromSrv => {
            this.urgencyLevels = urgencyLevelsFromSrv;
            resolve(true);
          });
      });

      Promise.all([sitesPromise, clientsPromise, typeTransportsPromise, urgencyLevelsPromise, addressPromise, currentUserPromise, driversPromise])
        .then(() => {
          this.isLoading = false;
          this.initForm();
          // Si l'utilisateur a une seule agence, la sélectionner dans le formulaire (sans déclencher chooseClient)
          if (this.isAdminWithOneClient && this.clients && this.clients.length === 1) {
            setTimeout(() => {
              this.selectedClient = this.clients[0];
              this.filteredSites = this.sites; // Les sites sont déjà chargés
              this.clientForm.get('client')!.setValue(this.clients[0], { emitEvent: false });
            }, 0);
          }
        });
    } else {
      Promise.all([clientsPromise, addressPromise, currentUserPromise, driversPromise])
        .then(() => {
          this.isLoading = false;
        });
    }
  }


  private initForm(): void {
    const startMoment: moment.Moment = moment(new Date());
    const startTime: string = startMoment.add(1, 'hours').format("HH:mm");

    this.editForm = this.fb.group({
      ordererComment: [null, [Validators.maxLength(this.commentMaxLength)]],
      // transportTypes: this.fb.array([]),
      type: ["isClient", Validators.required],
      urgencyLevel: new UntypedFormControl(null, [Validators.required]),
      schedule: this.fb.array([]),
      sender: this.fb.group({
        address: [null, [Validators.required]],
        otherAddress: [null],
        service: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
        ordererComment: [null, [Validators.maxLength(this.commentMaxLength)]],
        isPickup: [true],
        isDelivery: [false]
      }, Validators.required),
      receivers: this.fb.array([], Validators.required),
      ordererPhone: new UntypedFormControl(this.currentUser?.detail?.phone ? this.currentUser.detail.phone : null, [Validators.maxLength(this.stringMaxLength)]),
      ordererName: new UntypedFormControl(this.currentUser?.detail?.lastname || this.currentUser?.detail?.firstname ? this.currentUser?.detail?.lastname + ' ' + this.currentUser?.detail?.firstname : null, [Validators.maxLength(this.stringMaxLength)]),
      callDate: new UntypedFormControl(null),
      site: new UntypedFormControl(null, [Validators.required]),
      name: new UntypedFormControl(null),
      requestedDeliveryDate: new UntypedFormControl(null),
      requestedDeliveryTime: new UntypedFormControl(null)
    });

    this.initDateForm();
    this.onAddReceiver();


    this.editForm.valueChanges.subscribe(() => {
      this.invalidFields = this.findInvalidControls();
      this.invalidMessage = 'Le formulaire est incomplet/incorrect';
      this.invalidFields.map(field => {
        this.invalidMessage = this.invalidMessage + ' ' + field;
      })
    });

    this.editForm.get("site").valueChanges.subscribe((site: Site) => {
      this.canAddOtherAddress = !site || typeof site === 'string' || site.allowOtherAddress !== false;

      if (site && site.id) {
        this.addressesSrv.getListForSite(site.id).subscribe(addresses => {
          this.senders = addresses.filter(address => address.isSender);
          this.receivers = addresses.filter(address => address.isReceiver);

          this.editForm.get("sender.address").setValue(null);
          this.getArray("receivers").controls.forEach(control => { control.get("address").setValue(null) });
        });

        let clientId = this.clientForm.get('client')?.value?.id;

        this.dictionarySrv.getTransportListBySite(site, clientId).subscribe(transports => {
          this.allTransportTypes = transports;
          this.transportTypesSelected = [];
          this.filteredTransportTypes = of(transports);
        });

        this.dictionarySrv.getUrgencyListBySite(site, clientId).subscribe(urgencyLevels => {
          this.urgencyLevels = urgencyLevels;
          this.editForm.get("urgencyLevel").setValue(null);
        });
      }
    });



    this.editForm.get("type").valueChanges.subscribe(value => {
      if (value === "isTour")
        this.onTourSelected();
      else
        this.onTourUnselected();
    });

    // gérer la liste des services disponibles sur l'adresse sélectionnée
  }

  private onTourSelected() {
    this.updateAllStepType("isDelivery");
    this.addTourControls();
  }

  private onTourUnselected() {
    this.removeTourControls();
  }

  private updateAllStepType(type: string): void {
    this.getArray("receivers").controls.forEach(control => {
      control.get("type").setValue(type);
    });
  }

  private addTourControls(): void {
    if (this.canEditTourPrices) {
      this.editForm.addControl("price", new UntypedFormControl());
      this.editForm.addControl("extraStepPrice", new UntypedFormControl());
    }

    this.editForm.addControl("code", new UntypedFormControl(null, [Validators.maxLength(this.codeMaxLength)]));
    this.editForm.addControl("driver", new FormControl());
  }

  private removeTourControls(): void {
    if (this.canEditTourPrices) {
      this.editForm.removeControl("price");
      this.editForm.removeControl("extraStepPrice");
    }

    this.editForm.removeControl("code");
    this.editForm.removeControl("driver");
  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.editForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(this.translateFields(name));
      }
    }

    //check si le dernier receiver est bien une livraison
    if (!this.isTour()) {
      let lastReceiver = this.editForm.value.receivers[this.editForm.value.receivers.length - 1];

      if (lastReceiver.type === 'isPickup') {
        invalid.push(' | La dernière étape est un enlèvement');
      }
    }

    if (this.transportTypesSelected.length === 0) {
      invalid.push(' | Il n\'y a aucun type de produit');
    }

    return invalid;
  }

  translateFields(name): string {
    if (name === 'site') {
      return ' | Donneur d\'ordre';
    }
    if (name === 'urgencyLevel') {
      return ' | Niveau d\'urgence';
    }
    if (name === 'sender') {
      return this.isTour() ? '| étape 1 incomplète' : ' | Lieu d\'enlèvement incomplet';
    }
    if (name === 'receivers') {
      return this.isTour() ? '| étape(s) incomplète(s)' : ' | Destinataire(s) incomplet(s)';
    }
    if (name === 'patients') {
      return ' | Patient(s) incomplet(s)';
    }
  }

  minTransportType(min = 1): any {
    return (formArray: UntypedFormArray) => {
      return formArray.controls.length >= min ? null : { required: true };
    };
  }

  onAddTransportType(transportType?: Dictionary): void {
    this.transportTypesFields.push(
      this.fb.control(null, [Validators.required])
    );
  }

  removeTransportType(transportTypeToRemove): void {
    this.getArray('transportTypes').controls.splice(this.getArray('transportTypes').controls.indexOf(transportTypeToRemove), 1);
    this.editForm.value.transportTypes.splice(this.editForm.value.transportTypes.indexOf(transportTypeToRemove.value), 1);
  }

  get transportTypesFields(): UntypedFormArray {
    return this.editForm.get('transportTypes') as UntypedFormArray;
  }

  private scheduleToDate(schedule: any): Date {
    if (!schedule.startDate || !schedule.startTime || typeof schedule.startDate === 'string') {
      return null;
    }

    const startDate: moment.Moment = schedule.startDate.local();
    const startTime: number[] = schedule.startTime.split(':').map(s => parseInt(s, 10));
    return startDate.hours(startTime[0]).minutes(startTime[1]).utc().toDate();
  }

  initDateForm(): void {
    const startMoment: moment.Moment = moment(new Date());
    const startTime: string = moment(startMoment).add(20, 'minutes').format('HH:mm');
    const newGroup = this.fb.group({
      startDate: [startMoment, Validators.required],
      startTime: [startTime, Validators.required]
    });

    newGroup.controls.startDate.valueChanges.subscribe(() => {
      this.mustCheckDate = true;
    });

    newGroup.controls.startTime.valueChanges.subscribe(() => {
      this.mustCheckDate = true;
    });

    this.getArray('schedule').push(newGroup);
  }

  private minDateValidator(control: UntypedFormGroup): ValidationErrors | null {
    const scheduleDate = this.scheduleToDate(control.value);
    if (!this.mustCheckDate) {
      return null;
    }

    if (scheduleDate && scheduleDate < new Date()) {
      this.editForm.setErrors({ mismatch: true });
      return { mismatch: true };
    }

    return null;
  }

  resetStartDate(control) {
    control.get('startDate').reset();
  }

  resetBirthDate(control) {
    control.get('birthdate').reset();
  }

  onAddPatient(indexReceiver: number): void {

    const newGroup = this.fb.group({
      firstname: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      birthname: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      lastname: [null, [Validators.maxLength(this.stringMaxLength)]],
      birthdate: [null, [Validators.required]],
      productType: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      quantity: [null, [Validators.required]],
      ipp: [null, Validators.maxLength(this.stringMaxLength)]
    });

    this.getArrayPatients(indexReceiver).push(newGroup);
  }

  onCopyPatient(indexReceiver: number, control) {
    let patient = control.value;

    const newGroup = this.fb.group({
      firstname: [patient.firstname, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      birthname: [patient.birthname, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      lastname: [patient.lastname, [Validators.maxLength(this.stringMaxLength)]],
      birthdate: [patient.birthdate, [Validators.required]],
      productType: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      quantity: [null, [Validators.required]],
      ipp: [patient.ipp, Validators.maxLength(this.stringMaxLength)]
    });

    this.getArrayPatients(indexReceiver).push(newGroup);
  }

  getArrayPatients(index?: number) {
    let receivers = this.editForm.get('receivers') as UntypedFormArray;
    let patients = receivers.controls[index].get('patients') as UntypedFormArray;
    return patients;
  }

  onRemovePatient(indexReceiver: number, indexPatient: number): void {
    this.getArrayPatients(indexReceiver).removeAt(indexPatient);
  }

  isControlPatientSet(index: number): boolean {
    let receivers = this.editForm.get('receivers') as UntypedFormArray;

    return !!receivers.controls[index].get('patients');
  }

  onAddReceiver(): void {

    const newGroup = this.fb.group({
      address: [null, [Validators.required]],
      otherAddress: [null],
      service: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      ordererComment: [null, [Validators.maxLength(this.commentMaxLength)]],
      type: ['isDelivery', [Validators.required]],
      patients: this.fb.array([])
    });

    newGroup.get('type').valueChanges.subscribe(result => {
      if (result === 'isDelivery') {
        newGroup.addControl('patients', this.fb.array([]));
      } else {
        newGroup.removeControl('patients');
      }
    })

    this.getArray('receivers').push(newGroup);
  }

  onRemoveReceiver(index: number): void {
    this.getArray('receivers').removeAt(index);
  }

  onCheckboxChange(e): any {
    const checkArray: UntypedFormArray = this.editForm.get('transportTypes') as UntypedFormArray;

    if (e.checked) {
      checkArray.push(new UntypedFormControl(e.source.value));
    } else {
      let i: number = 0;
      checkArray.controls.forEach((item: UntypedFormControl) => {
        if (item.value === e.source.value) {
          checkArray.removeAt(i);
          return;
        }
        i++;
      });
    }
  }

  remove(fruit: DictionaryValue): void {
    const index = this.transportTypesSelected.indexOf(fruit);

    if (index >= 0) {
      this.transportTypesSelected.splice(index, 1);
    }

    this.checkIfSelected();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    document.getElementById("deviceChipList").blur();
    if (!this.transportTypesSelected.includes(event.option.value)) {
      this.transportTypesSelected.push(event.option.value);
      // this.devices = this._filter(event.option.value);
      this.checkIfSelected();

      this.deviceInput.nativeElement.value = '';
    }
  }

  checkIfSelected() {
    this.filteredTransportTypes = this.filteredTransportTypes.pipe(
      map(array => {
        return array.map(element => {
          if (this.transportTypesSelected.includes(element)) {
            element.selected = true;
          } else {
            element.selected = false;
          }
          return element;
        })

      })
    )
  }

  private _filter(value?): DictionaryValue[] {
    let filterValue = '';

    if (value.name) {
      filterValue = value.name.toLowerCase();
      return this.allTransportTypes.filter(el => {
        return el.id === value.id;
      });
    } else {
      filterValue = value.toLowerCase();
      return this.allTransportTypes.filter(transportType => transportType.name.toLowerCase().indexOf(filterValue) === 0);
    }
  }

  clearDevicesSelected(): void {
    this.transportTypesSelected = [];
    this.filteredTransportTypes.pipe(
      map(fruit => {
        return this.allTransportTypes;
      })
    )
    this.checkIfSelected();
  }

  getClientControl(control: string): UntypedFormControl {
    return this.clientForm.get(control) as UntypedFormControl;
  }

  chooseClient(client: Client) {
    this.isLoading = true;
    this.selectedClient = client;
    let clientId = client.id;
    let clientArray = [clientId];
    this.filteredSites = [];

    // Réinitialiser le site sélectionné
    if (this.editForm) {
      this.editForm.get('site').setValue(null);
      this.editForm.get('urgencyLevel').setValue(null);
    }
    if (this.transportTypesSelected.length > 0) {
      this.transportTypesSelected = [];
    }

    const sitesPromise = new Promise((resolve, reject) => {
      this.sitesSrv.getNonTransmissionOnlyListByClients(clientArray)
        .subscribe(sitesFromSrv => {
          this.filteredSites = sitesFromSrv;
          resolve(true);
        });
    });

    const typeTransportsPromise = new Promise((resolve, reject) => {
      this.dictionarySrv.getTransportListById2(clientId)
        .subscribe(transportsFromSrv => {
          this.allTransportTypes = transportsFromSrv;
          this.filteredTransportTypes = this.transportTypesCtrl.valueChanges.pipe(
            startWith(null),
            map((fruit: string | null) => fruit ? this._filter(fruit) : this.allTransportTypes.slice()));
          resolve(true);
        });
    });

    const urgencyLevelsPromise = new Promise((resolve, reject) => {
      this.dictionarySrv.getUrgencyListById2(clientId)
        .subscribe(urgencyLevelsFromSrv => {
          this.urgencyLevels = urgencyLevelsFromSrv;
          resolve(true);
        });
    });


    Promise.all([sitesPromise, typeTransportsPromise, urgencyLevelsPromise])
      .then(() => {
        this.isLoading = false;
        if (!this.canChooseValue) {
          this.canChooseValue = true;
          this.initForm();
        }
      });
  }

  showLabileText(labileBlood: LabileBlood): string {
    return 'labileBlood.' + LabileBlood[labileBlood];
  }

  public isTour(): boolean {
    return this.getControl('type').value === 'isTour';
  }

  public canCreateTour(): boolean {
    const allowedRoles = [
      RoleName.bizzdev,
      RoleName.super_admin,
      RoleName.driver,
      RoleName.dispatcher,
      RoleName.admin,
    ];

    return this.authSrv.isAuthorized(allowedRoles);
  }

  isTypeOf(type: string, value: any): boolean {
    return typeof value === type;
  }
}
